import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';

import {
  Button,
} from '@lockerpm/design';

import authComponents from "./components";
import commonComponents from "../../components/common";

import authServices from "../../services/auth";
import commonServices from "../../services/common";

import common from "../../utils/common";
import global from "../../config/global";

const Lock = () => {
  const { BusyOverlay } = commonComponents;
  const { AuthCard, GetMPHintModal, JoinEnterprise, LockForm } = authComponents;

  const { t } = useTranslation();
  const navigate = useNavigate();

  const userInfo = useSelector((state) => state.auth.userInfo);
  const isLoading = useSelector((state) => state.system.isLoading);
  const isConnected = useSelector((state) => state.service.isConnected);
  const isDesktopConnected = useSelector((state) => state.service.isDesktopConnected)
  const cacheData = useSelector((state) => state.service.cacheData);
  const enterpriseInvitations = useSelector((state) => state.enterprise.enterpriseInvitations);

  const [loading, setLoading] = useState(true);
  const [callingAPI, setCallingAPI] = useState(false);
  const [mpHintVisible, setMPHintVisible] = useState(false);
  const [logging, setLogging] = useState(false);
  const [isPair, setIsPair] = useState(false);
  const [serviceUser, setServiceUser] = useState(null);
  const [step, setStep] = useState(1);
  const [otherMethod, setOtherMethod] = useState('password');
  const [requestOrJoinOrg, setRequestOrJoinOrg] = useState(false);
  const [unlockValues, setUnlockValues] = useState({});

  const query = common.convertStringToQuery(window.location.search);

  const isUnlockByDesktop = useMemo(() => {
    return userInfo?.sync_all_platforms && cacheData?.email === userInfo?.email && !!cacheData?.unlock_method
  }, [userInfo?.sync_all_platforms, userInfo?.email, cacheData, isConnected])

  const enterpriseInvitation = useMemo(() => {
    return enterpriseInvitations[0]
  }, [enterpriseInvitations])

  const containerRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (!e.isTrusted) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    };

    const container = containerRef?.current;
    if (container) {
      container.addEventListener("click", handleClick, true); 
    }

    return () => {
      if (container) {
        container.removeEventListener("click", handleClick, true);
      }
    };
  }, []);

  useEffect(() => {
    initState();
    fetchData();
  }, [])

  useEffect(() => {
    if (userInfo?.email) {
      const isUnLocked = common.getIsUnlocked();
      if (userInfo?.timeout_action === global.constants.TIMEOUT_ACTION.LOGOUT && userInfo?.timeout === -1 && isUnLocked == 1) {
        authServices.logout(true);
      } else if (userInfo?.block || (!userInfo?.is_factor2 && userInfo?.is_require_2fa)) {
        global.navigate(global.keys.SETUP_2FA)
      } else if (!userInfo?.is_pwd_manager) {
        global.navigate(global.keys.CREATE_MASTER_PASSWORD)
      } else if (userInfo.is_require_passwordless && userInfo.login_method === 'password') {
        global.navigate(global.keys.SETUP_PASSWORDLESS, {}, query)
      } else if (isUnlockByDesktop) {
        getServiceUser();
      }
    }
  }, [userInfo?.email, isUnlockByDesktop])

  const initState = () => {
    setCallingAPI(false);
    setMPHintVisible(false);
    setIsPair(false);
    setServiceUser(null);
    setStep(1);
    setOtherMethod('password')
  }

  const fetchData = async () => {
    setLoading(true);
    await common.fetchUserInfo();
    setLoading(false);
  }

  const handleLogout = async () => {
    setLogging(true);
    await authServices.logout(true);
    setLogging(false);
  }

  const getServiceUser = async () => {
    setLoading(true);
    setIsPair(isDesktopConnected && !service.hasPairingKey)
    if (service.hasPairingKey) {
      try {
        const serviceUser = await service.getCurrentUser();
        if (serviceUser) {
          setOtherMethod(cacheData?.unlock_method || null);
          setServiceUser(serviceUser);
        } else {
          setStep(1)
        }
      } catch (error) {
        await commonServices.reset_service();
        setStep(1)
      }
    } else {
      setStep(1)
    }
    setLoading(false)
  }

  const handleSubmit = async (values) => {
    if (enterpriseInvitation?.domain != null) {
      setUnlockValues(values);
      setRequestOrJoinOrg(true);
    } else {
      setUnlockValues({});
      setRequestOrJoinOrg(false);
      await handleUnlock(values);
    }
  }

  const handleUnlock = async (values = {
    unlock_method,
    password,
    hashedPassword,
    pwl_id,
    kdf,
    kdf_iterations,
    kdf_memory,
    kdf_parallelism
  }) => {
    setCallingAPI(true);
    const returnUrl = query?.return_url ? decodeURIComponent(query?.return_url) : '/';
    const payload = {
      username: userInfo.email,
      email: userInfo.email,
      password: values.password,
      hashedPassword: values.hashedPassword,
      pwl_id: values.pwl_id,
      kdf: values.kdf,
      kdf_iterations: values.kdf_iterations,
      kdf_memory: values.kdf_memory,
      kdf_parallelism: values.kdf_parallelism,
      keyB64: values.key,
      unlock_method: values.unlock_method || otherMethod
    }
    await common.unlockToVault(payload, query, () => navigate(returnUrl, { replace: true })).then(() => {
      common.getResourceCountries();
      common.getResourcePlans();
      common.setIsUnlocked();
    }).catch((error) => {
      const { code } = common.getErrorMessage(error);
      if (code == 1012) {
        global.navigate(global.keys.SETUP_2FA)
      }
      global.pushError(error)
    });
    setCallingAPI(false)
  }

  return (
    <div
      className="auth-page"
      ref={containerRef}
    >
      {
        (isLoading || loading) && <BusyOverlay />
      }
      {
        !(isLoading || loading) && <AuthCard
          className="w-[600px]"
          other={
            requestOrJoinOrg ? <div className="mt-4 text-center">
              <span>
                {t('setup_2fa.note')}
                <Button
                  type="link"
                  className="font-semibold"
                  onClick={handleLogout}
                >
                  {t('button.logout')}
                </Button>
              </span>
            </div> : <></>
          }
        >
          <div className="flex items-center justify-center">
            {
              requestOrJoinOrg ? <JoinEnterprise
                enterpriseInvitation={enterpriseInvitation}
                onUnlock={() => handleUnlock(unlockValues)}
              /> : <div className="w-full">
                <LockForm
                  step={step}
                  isPair={isPair}
                  logging={logging}
                  loading={loading}
                  callingAPI={callingAPI}
                  serviceUser={serviceUser}
                  otherMethod={otherMethod}
                  isShowMPHint={true}
                  isUnlockByDesktop={isUnlockByDesktop}
                  setStep={setStep}
                  setIsPair={setIsPair}
                  setServiceUser={setServiceUser}
                  setOtherMethod={setOtherMethod}
                  setMPHintVisible={setMPHintVisible}
                  onSubmit={handleSubmit}
                  onLogout={handleLogout}
                />
              </div>
            }
          </div>
        </AuthCard>
      }
      <GetMPHintModal
        visible={mpHintVisible}
        userInfo={userInfo}
        onClose={() => setMPHintVisible(false)}
      />
    </div>
  );
}

export default Lock;